import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import PageHeader from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import {
  HandCoins, Users, TrendingUp, Trophy, Rocket, MapPin,
  Smartphone, DollarSign, ArrowRight, Sparkles, Radio,
} from 'lucide-react';

const PERKS = [
  { icon: DollarSign, title: '8-15% Commission', desc: 'Earn 8% on every service booking and 15% on every shop product referred through your link or code.' },
  { icon: Smartphone, title: 'Track Everything Live', desc: 'Personal dashboard with real-time referral count, pending commissions, and payout history.' },
  { icon: MapPin, title: 'Sell On The Ground', desc: 'Hand out a unique code — walk-ups, door-knocks, community events. Your customers just say the code at checkout.' },
  { icon: Radio, title: 'Sell Online', desc: 'Unique share link auto-attributes every click for 30 days. Post on social, email, or in your bio.' },
  { icon: Trophy, title: 'Public Leaderboard', desc: 'Top reps get monthly bonuses and featured recognition on the Royal Palm site.' },
  { icon: Sparkles, title: 'Marketing Kit Included', desc: 'Download branded flyers, social graphics, and scripts — everything you need to close.' },
];

export default function AffiliateLandingPage() {
  const [top, setTop] = useState([]);
  useEffect(() => {
    api.get('/affiliate/leaderboard?limit=5').then(r => setTop(r.data.rows || [])).catch(() => {});
  }, []);

  return (
    <Layout>
      <PageHeader
        eyebrow="Affiliate Program"
        title="Represent Royal Palm — Get Paid Every Referral"
        subtitle="Sell services and products on the ground or online. Simple commissions, weekly payouts, no cap."
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="affiliate-landing">
        {/* Hero CTA */}
        <div className="grid md:grid-cols-2 gap-6 items-center mb-14">
          <div>
            <Badge className="bg-[#C8922F]/15 text-[#C8922F] hover:bg-[#C8922F]/15 rounded-full mb-3">Now recruiting</Badge>
            <h2 className="font-headline text-3xl md:text-4xl text-[#17352D] leading-tight">Turn every conversation into a paycheck.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-lg">South Florida is booming — homeowners need painting, landscaping, pressure washing, remodels, and hurricane prep every week. Talk to them, hand them your code, get paid when they book.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white" data-testid="affiliate-apply-cta">
                <Link to="/affiliate/apply">Apply Now <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-xl" data-testid="affiliate-leaderboard-cta">
                <Link to="/affiliate/leaderboard">View Leaderboard <Trophy className="h-4 w-4 ml-1" /></Link>
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> No cap on referrals</span>
              <span className="flex items-center gap-1"><Rocket className="h-3.5 w-3.5" /> Same-week payouts</span>
              <span className="flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> Free to join</span>
            </div>
          </div>
          <Card className="rounded-3xl border-[#E6E9EE] bg-gradient-to-br from-[#17352D] to-[#1E4338] text-white overflow-hidden">
            <CardContent className="p-8">
              <HandCoins className="h-8 w-8 text-[#EED7A6] mb-4" />
              <div className="text-xs uppercase tracking-[0.22em] text-[#EED7A6]">Sample Payout</div>
              <div className="font-headline text-4xl mt-2">$1,140<span className="text-lg text-white/60">/mo</span></div>
              <p className="text-sm text-white/70 mt-2">If you refer just 3 landscaping jobs ($3k avg) and 6 shop items ($120 avg) each month.</p>
              <div className="mt-6 divide-y divide-white/10 text-sm">
                <div className="py-2 flex justify-between"><span>3 service jobs × 8%</span><span className="font-semibold">$720</span></div>
                <div className="py-2 flex justify-between"><span>6 shop items × 15%</span><span className="font-semibold">$108</span></div>
                <div className="py-2 flex justify-between"><span>Repeat customers bonus</span><span className="font-semibold text-[#EED7A6]">$312</span></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Perks */}
        <div className="mb-14">
          <div className="text-xs uppercase tracking-[0.22em] text-[#C8922F] font-semibold">What you get</div>
          <h2 className="font-headline text-2xl md:text-3xl text-[#17352D] mt-1">Everything you need to close, with none of the overhead</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {PERKS.map(p => (
              <Card key={p.title} className="rounded-2xl border-[#E6E9EE]">
                <CardContent className="p-5">
                  <div className="h-10 w-10 rounded-xl bg-[#F2F7F5] text-[#17352D] flex items-center justify-center">
                    <p.icon className="h-5 w-5" />
                  </div>
                  <div className="font-headline text-lg text-[#17352D] mt-3">{p.title}</div>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{p.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div className="mb-14">
          <div className="text-xs uppercase tracking-[0.22em] text-[#C8922F] font-semibold">How it works</div>
          <div className="grid md:grid-cols-4 gap-4 mt-4">
            {[
              { n: 1, t: 'Apply', d: 'Fill out a 2-minute form. Approvals typically within 24-48h.' },
              { n: 2, t: 'Get your code + link', d: 'Unique code like ROYAL-JASON123 and a personal referral URL.' },
              { n: 3, t: 'Share everywhere', d: 'Ground: hand out cards. Online: post link on IG, TikTok, email.' },
              { n: 4, t: 'Get paid', d: 'Watch commissions roll in — payouts via Zelle, CashApp, or check.' },
            ].map(s => (
              <div key={s.n} className="rounded-2xl border border-[#E6E9EE] bg-white p-5">
                <div className="h-8 w-8 rounded-full bg-[#17352D] text-white font-bold flex items-center justify-center">{s.n}</div>
                <div className="font-headline text-lg text-[#17352D] mt-3">{s.t}</div>
                <p className="text-sm text-slate-600 mt-1">{s.d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Live leaderboard preview */}
        {top.length > 0 && (
          <Card className="rounded-3xl border-[#E6E9EE] bg-white mb-14">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#C8922F] font-semibold">This month&apos;s top reps</div>
                  <h3 className="font-headline text-2xl text-[#17352D] mt-0.5">Leaderboard</h3>
                </div>
                <Button asChild variant="outline" className="rounded-xl"><Link to="/affiliate/leaderboard">See full board</Link></Button>
              </div>
              <div className="mt-4 divide-y divide-[#E6E9EE]">
                {top.map((r) => (
                  <div key={r.code} className="py-3 flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${r.rank === 1 ? 'bg-[#C8922F] text-white' : r.rank <= 3 ? 'bg-[#EED7A6] text-[#17352D]' : 'bg-slate-100 text-slate-600'}`}>#{r.rank}</div>
                    <div className="flex-1">
                      <div className="font-semibold text-[#17352D]">{r.name}</div>
                      <div className="text-xs text-slate-500">{r.city || '—'} · {r.lifetime_referrals} referrals</div>
                    </div>
                    <div className="font-headline text-lg text-[#C8922F]">${r.lifetime_earned.toFixed(0)}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA */}
        <Card className="rounded-3xl border-[#E6E9EE] bg-[#17352D] text-white overflow-hidden">
          <CardContent className="p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.22em] text-[#EED7A6] font-semibold">Ready?</div>
              <h3 className="font-headline text-2xl md:text-3xl mt-1">Apply in 2 minutes. Earn for years.</h3>
              <p className="text-sm text-white/80 mt-2">No experience required. If you know people in South Florida, you already qualify.</p>
            </div>
            <Button asChild size="lg" className="rounded-xl bg-[#C8922F] hover:bg-[#B27F26] text-white" data-testid="affiliate-apply-bottom-cta">
              <Link to="/affiliate/apply">Start My Application <ArrowRight className="h-4 w-4 ml-1" /></Link>
            </Button>
          </CardContent>
        </Card>
      </section>
    </Layout>
  );
}
